import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Copy, Lock, Paperclip, Send, ShieldCheck, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { getErrorMessage } from '../../lib/apiClient';
import { formatBytes } from '../../utils/format';
import { createSecret, messageLink } from './secretsApi';

const EXPIRY_OPTIONS = [
  { label: '1 hour', value: 3600 },
  { label: '24 hours', value: 86400 },
  { label: '7 days', value: 604800 },
  { label: '30 days', value: 2592000 },
  { label: 'No expiry', value: 0 },
];

export default function ComposeMessagePage() {
  const fileInput = useRef(null);
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [files, setFiles] = useState([]);
  const [maxViews, setMaxViews] = useState(1);
  const [expiresInSeconds, setExpiresInSeconds] = useState(86400);
  const [password, setPassword] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [result, setResult] = useState(null);
  // Attachment upload progress while sending: overall % and per-file %.
  const [progress, setProgress] = useState(null);
  const [fileProgress, setFileProgress] = useState({});
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      setProgress(files.length ? 0 : null);
      setFileProgress({});
      return createSecret({
        name: label,
        value,
        password: password || undefined,
        expiresInSeconds: expiresInSeconds || undefined,
        maxViews: Number(maxViews) || undefined,
        oneTime: Number(maxViews) === 1,
        recipientEmail: recipientEmail || undefined,
        files,
        onProgress: setProgress,
        onFileProgress: (index, percent) => setFileProgress((p) => ({ ...p, [index]: percent })),
      });
    },
    onSettled: () => setProgress(null),
    onSuccess: (data) => setResult(data),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not create the message')),
  });

  const addFiles = (list) => {
    // Snapshot synchronously: FileList is LIVE and the input's value is cleared
    // right after this call — a deferred Array.from would see an empty list.
    const picked = Array.from(list);
    // Dedupe re-added files by name+size; the backend caps attachments at 10.
    const merged = [...files];
    for (const file of picked) {
      if (!merged.some((f) => f.name === file.name && f.size === file.size)) merged.push(file);
    }
    if (merged.length > 10) toast.error('Up to 10 files per message — extra files were skipped');
    setFiles(merged.slice(0, 10));
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const copyLink = async () => {
    await navigator.clipboard.writeText(messageLink(result.token));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const reset = () => {
    setResult(null);
    setValue('');
    setLabel('');
    setFiles([]);
    setPassword('');
    setRecipientEmail('');
    setMaxViews(1);
  };

  if (result) {
    const link = messageLink(result.token);
    return (
      <div className="mx-auto max-w-xl">
        <div className="rounded-2xl border border-slate-200 bg-slate-100 p-6 text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-emerald-500" />
          <h1 className="mt-3 text-lg font-semibold text-slate-800">Your encrypted link is ready</h1>
          <p className="mt-1 text-sm text-slate-500">
            Share this link. It self-destructs after {maxViews} view{maxViews > 1 ? 's' : ''}
            {result.attachmentCount > 0
              ? ` and includes ${result.attachmentCount} encrypted attachment${
                  result.attachmentCount > 1 ? 's' : ''
                }`
              : ''}
            .
          </p>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
            <input
              readOnly
              value={link}
              className="min-w-0 flex-1 bg-transparent px-2 text-sm text-slate-700 focus:outline-none"
            />
            <Button size="sm" variant="secondary" onClick={copyLink}>
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          {recipientEmail && (
            <p className="mt-2 text-xs text-slate-400">A link was emailed to {recipientEmail}.</p>
          )}
          <div className="mt-5 flex justify-center gap-2">
            <Button onClick={reset}>Send another</Button>
            <Link to="/messages/sent">
              <Button variant="secondary">View sent messages</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-brand-600" />
          <h1 className="text-xl font-semibold text-slate-800">Send an encrypted message</h1>
        </div>
        <Link
          to="/messages/sent"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-400"
        >
          <Send className="h-4 w-4" />
          Sent messages
        </Link>
      </div>

      <form
        className="space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Message</label>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
            rows={5}
            maxLength={65536}
            placeholder="Type your secret message…"
            className="block w-full resize-y rounded-lg border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-600 focus:outline-none"
          />
        </div>

        {/* Attachments */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">
              Attachments (optional)
              <span className="ml-1 font-normal text-slate-400">· up to 10 files</span>
            </label>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-400"
            >
              <Paperclip className="h-4 w-4" />
              Add files
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
          {files.length > 0 && (
            <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <Paperclip className="h-4 w-4 text-slate-400" />
                  <span className="flex-1 truncate text-slate-700">{f.name}</span>
                  {mutation.isPending && fileProgress[i] != null && (
                    <span className="text-xs font-medium text-brand-600">{fileProgress[i]}%</span>
                  )}
                  <span className="text-xs text-slate-400">{formatBytes(f.size)}</span>
                  {!mutation.isPending && (
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="rounded p-1 text-slate-400 hover:text-red-500"
                      aria-label="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Max views"
            type="number"
            min={1}
            value={maxViews}
            onChange={(e) => setMaxViews(e.target.value)}
            hint="Self-destructs after this many opens"
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Expires</label>
            <Select
              value={expiresInSeconds}
              onChange={(v) => setExpiresInSeconds(Number(v))}
              options={EXPIRY_OPTIONS}
              ariaLabel="Expires"
              className="w-full"
            />
          </div>
        </div>

        <Input
          label="Password (optional)"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Recipient must enter this to open the message"
        />
        <Input
          label="Send Encrypted Message To (Recipient Email)"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="recipient@example.com"
        />
        <Input
          label="Label (only you see this)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Encrypted message"
        />

        <Button type="submit" className="w-full" loading={mutation.isPending} disabled={!value.trim()}>
          <Lock className="h-4 w-4" />
          {mutation.isPending && progress !== null
            ? `Encrypting & uploading… ${progress}%`
            : 'Generate secure link'}
        </Button>
      </form>
    </div>
  );
}
